import React, { useMemo } from 'react';
import { DeleteOutline } from '@material-ui/icons';
import EventLabel from '../../../components/EventLabel/EventLabel';
import Accordion from '../../../components/Accordion';
import BookingDetails from './BookingDetails';
import { displayMoney, toStringTwoDecimals, roundToNearestHalf } from '../../../utils/money.utils';
import { Tooltip } from 'reactstrap';
import { useStore } from '../../../store/Store';
import cx from 'classnames';

export default function BookingItem(props) {
  const {
    hotel,
    dateRangeString,
    type,
    nights,
    imageUrl,
    comment,
    hotelGroupId,
    displayEventSettings = {},
    price,
    rooms,
    currency,
    onBookingChange,
    onBookingDelete,
    vatPercent = 0,
    roomMates,
    orderRowId,
    description,
    supplementPrice,
    supplementVatPercent,
    selectRoomMateFromOrder,
  } = props;

  const { name, roomSize } = hotel;

  const store = useStore();

  const formattedPrice = useMemo(() => {
    const discountAmount = 0;
    const incVat = displayEventSettings?.['ShowEventIncVat'];
    const newPrice = parseFloat(incVat ? displayMoney(parseFloat(price - discountAmount), vatPercent) : price - discountAmount);

    const additionalAmount =
      (supplementPrice ? parseFloat(incVat ? displayMoney(parseFloat(supplementPrice), supplementVatPercent) : supplementPrice) * nights : 0) * roomSize;

    return (
      <span>
        {toStringTwoDecimals(newPrice + additionalAmount)}
        <span className="mx-1 text-sm">{currency}</span>
        <span className="mr-1 text-xs text-neutral-500">
          {incVat ? <EventLabel name="IncVat" groupName="CMS.Hotel" /> : <EventLabel name="ExVat" groupName="CMS.Hotel" />}
        </span>
      </span>
    );
  }, [price, vatPercent, displayEventSettings, currency, supplementPrice, supplementVatPercent, nights, roomSize]);

  const bgStyle =
    document.querySelector('.content-wrapper.cms-hotel') && getComputedStyle(document.querySelector('.content-wrapper.cms-hotel'))?.backgroundColor;

  return (
    <div
      className="booking-item basis-full flex-col flex my-6 relative bg-white rounded-md overflow-hidden"
      style={{
        backgroundColor: bgStyle ?? '#fff',
      }}
    >
      <div className="flex flex-col 2xl:flex-row rounded-t-md  border-divider border">
        <div className="min-w-[250px] w-full 2xl:max-w-[250px] h-auto overflow-hidden  sm:rounded-tl-md rounded-bl-none">
          <img src={imageUrl} alt={name} className="object-cover h-full w-full" />
        </div>
        <div className='border-l border-slate-300'>
          <div className="mt-2 w-full  2xl:mt-0 flex flex-col justify-between gap-y-2">
            <div className="flex items-start flex-col justify-start p-2">
              <span className="price 2xl:text-right font-bold text-lg">{displayEventSettings?.['ShowEventPrice'] && formattedPrice}</span>
              <span className="nightly-price 2xl:text-right font-normal">
                {nights} <EventLabel name="Nights" groupName="CMS.Hotel" />
              </span>
            </div>
          </div>
          <div className="flex w-full 2xl:w-full flex-col 2xl:flex-row justify-between p-2">
            <div className="flex w-full  flex-col justify-between">
              <div className="flex flex-col">
                <span className="title semi-bold " style={{ marginBottom: 0 }}>
                  {name}
                </span>
                <span className="room-type font-normal">{type}</span>
                <span className="font-normal">
                  <EventLabel name="Beds" groupName="CMS.Hotel" />: {roomSize}
                </span>
                <span className="font-normal">
                  <i className="ri-calendar-line mr-2" />
                  {dateRangeString}
                </span>
                <span className="font-normal text-sm  mt-2">{hotel?.description}</span>
              </div>
            </div>
          </div>
          <div className="mt-2 w-full p-2 2xl:mt-0 flex flex-col justify-between gap-y-2 border-t border-t-slate-200">
            <div className={cx('flex items-end md:justify-end  justify-start')}>
              {(!(store?.orders.find(o => o.leader) ?? store.orders?.[0])?.badgeNo || orderRowId < 0) && (
                <button
                  onClick={onBookingDelete}
                  className="px-3 py-2.5 m-0  md:ml-2 bg-danger text-white rounded-md  flex text-sm  h-fit w-max items-center justify-center"
                >
                  <i className="ri-delete-bin-line mr-1" /> <EventLabel name="DeleteRoom" groupName="CMS.Hotel" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Accordion
        index={0}
        defaultOpen
        className="border-x border-divider border-b"
        header={
          <span className={cx('text-md flex gap-x-2  w-full items-center justify-between ', rooms?.length >= roomSize ? 'py-1' : '')}>
            <span className="font-semibold">
              <i className="ri-information-line mr-2" />
              <EventLabel name="LivingDetails" groupName="CMS.Hotel" />
            </span>
            {rooms?.length < roomSize && (
              <div className="text-amber-900 bg-amber-100  border border-amber-200  flex items-center  gap-x-2 px-2 py-2 rounded-lg font-normal  leading-1 text-xs ">
                <i className="ri-information-line text-amber-500 font-bold"></i>
                <EventLabel name="PleaseAddPeopleToRoom" groupName="CMS.Hotel" />
              </div>
            )}
          </span>
        }
      >
        <BookingDetails
          {...props}
          comment={comment}
          hotelGroupId={hotelGroupId}
          onBookingChange={onBookingChange}
          roomMates={roomMates}
          selectRoomMateFromOrder={selectRoomMateFromOrder}
        />
      </Accordion>
    </div>
  );
}
